import { useQuery } from '@powersync/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  conversionTargets,
  createSubtask,
  linkUrlOf,
  normalizeTagName,
  setBlockTags,
  setFollowUp,
  updateBlockBody,
  updateBlockDates,
  updateBlockTitle,
} from '@flow/domain';
import {
  queries,
  type BlockDetailRow,
  type BlockTagRow,
  type CaptureDestinationRow,
  type FieldDefRow,
  type FieldValueRow,
  type HighlightTypeRow,
  type LocalQuery,
  type TaxonomyTagRow,
  type WorkspaceTaxonomyRow,
} from '@flow/sync';
import { DetailPanel, type DetailLayout, type DetailTaxonomy } from '@flow/ui/components';

import type { LocalDatabase } from '../db/powersync.js';
import { t } from '../i18n/es.js';
import { useScreenCommands } from '../shell/AppLayout.jsx';
import type { Command } from '../shell/commands.js';

import type { TaskActions, TaskBlock, TaskChild } from './use-task-actions.jsx';

/**
 * The detail panel with its data (f1-detail-panel): reads the block, its tags, its custom fields
 * and its subtasks from the local database, and writes every change with a domain command. The
 * actions shared with the list (complete, TAI, move, convert) come from `useTaskActions`. After a
 * conversion the panel stays on the block, whatever its new kind (f1-type-conversion D5).
 */

interface ChildRow {
  id: string;
  parent_id: string;
  title: string;
  status: string | null;
  kind: string;
  sort_key: string;
  completed_at: string | null;
}

const useRows = <T,>(query: LocalQuery | null) => {
  const q = query ?? queries.none();
  return useQuery<T>(q.sql, q.params);
};

export interface TaskDetailProps {
  blockId: string;
  userId: string;
  local: LocalDatabase;
  workspaceId: string;
  layout: DetailLayout;
  stages: CaptureDestinationRow[];
  /** The type of highlight of the view, when it has one. */
  highlightType: HighlightTypeRow | undefined;
  highlighted: boolean;
  actions: TaskActions;
  onOpen: (id: string) => void;
  onClose: () => void;
  /** The task is no longer visible (trashed, or it left the workspace). */
  onGone: () => void;
  /**
   * The panel shows tasks the list does not have (a subtask, or a task that left the view): it
   * hands them to the shared actions so completing or moving them works from here too.
   */
  register: (blocks: TaskBlock[], children: Record<string, TaskChild[]>) => void;
  today: string;
  /** Number of highlights used in the period, for «Ya usas n de m». */
  highlightsUsed: number;
}

export function TaskDetail(props: TaskDetailProps) {
  const { data: blocks } = useRows<BlockDetailRow>(queries.blockDetail(props.blockId));
  const block = blocks[0];
  // «No rows» means «not yet» until the task has been seen once: after a reload the local
  // database may still be syncing, and on this browser it starts empty.
  const [seen, setSeen] = useState(false);
  if (block && !seen) setSeen(true);
  const { data: tags } = useRows<BlockTagRow>(queries.blockTagsOf(props.blockId));
  const { data: allTags } = useRows<TaxonomyTagRow>(queries.workspaceTags(props.workspaceId));
  const { data: taxonomies } = useRows<WorkspaceTaxonomyRow>(
    queries.workspaceTaxonomies(props.workspaceId),
  );
  const { data: fieldDefs } = useRows<FieldDefRow>(queries.fieldDefs(props.workspaceId));
  const { data: fieldValues } = useRows<FieldValueRow>(queries.blockFieldValues(props.blockId));
  const { data: children } = useRows<ChildRow>(queries.subtasksOf(props.blockId));

  const stage = props.stages.find((row) => row.id === block?.stage_id);
  const register = props.register;
  useEffect(() => {
    if (!block) return;
    const asTask = (row: BlockDetailRow | ChildRow): TaskBlock => ({
      id: row.id,
      workspace_id: block.workspace_id,
      title: row.title,
      kind: row.kind,
      status: row.status,
      stage_id: 'stage_id' in row ? row.stage_id : null,
      stage_changed_at: 'stage_changed_at' in row ? row.stage_changed_at : null,
      completed_at: row.completed_at,
      due_on: 'due_on' in row ? row.due_on : null,
      start_on: 'start_on' in row ? row.start_on : null,
      parent_id: row.parent_id,
      body: 'body' in row ? row.body : null,
      props: 'props' in row ? row.props : null,
    });
    register([asTask(block), ...children.map(asTask)], {
      [block.id]: children.map((child) => ({
        id: child.id,
        parent_id: child.parent_id,
        kind: child.kind,
        status: child.status,
        completed_at: child.completed_at,
      })),
    });
  }, [block, children, register]);
  // The query does not order (watched-query rule); the handful of rows is ordered here.
  const subtasks = useMemo(
    () => [...children].sort((a, b) => (a.sort_key < b.sort_key ? -1 : 1)),
    [children],
  );

  /**
   * The panel closes on its own when the task goes to the trash or stops being visible. It waits
   * a moment first: while a sync batch is applied the row can be missing for an instant, and
   * closing then would also drop the task from the URL.
   */
  // «Convertir en…» in ⌘K while the panel is open (D4); the actions change on every write.
  const actionsRef = useRef(props.actions);
  const closeRef = useRef(props.onClose);
  useEffect(() => {
    actionsRef.current = props.actions;
    closeRef.current = props.onClose;
  });
  const targets = useMemo(
    () => (block ? conversionTargets(block.kind, stage?.category ?? null) : []),
    [block, stage?.category],
  );
  const blockId = block?.id;
  const commands = useMemo<Command[]>(
    () =>
      blockId
        ? [
            ...targets.map((target) => ({
              id: `convert:${target}`,
              title: t.convert.command(t.convert.kinds[target] ?? target),
              group: t.shell.commands.actions,
              run: () => actionsRef.current.convert(blockId, target),
            })),
            // «Eliminar» the open block (f1-trash-search D7): to the trash, and the panel closes.
            {
              id: 'trash',
              title: t.trash.command,
              group: t.shell.commands.actions,
              run: () => {
                void actionsRef.current.trash(blockId);
                closeRef.current();
              },
            },
          ]
        : [],
    [blockId, targets],
  );
  useScreenCommands(commands);

  const gone = seen && !block;
  const onGone = props.onGone;
  useEffect(() => {
    if (!gone) return undefined;
    const timer = setTimeout(() => onGone(), 1500);
    return () => clearTimeout(timer);
  }, [gone, onGone]);
  if (!block) return null;

  const run = (operations: Parameters<TaskActions['run']>[0]) => props.actions.run(operations);

  const tagsOf = (taxonomyId: string) =>
    tags
      .filter((row) => row.taxonomy_id === taxonomyId)
      .map((row) => ({ id: row.tag_id, name: row.name }));

  const taxonomyFields: DetailTaxonomy[] = taxonomies
    // `waiting_on` is a field of its own: it is shown with the follow-up date.
    .filter((taxonomy) => taxonomy.key !== 'person' || stage?.category !== 'waiting')
    .map((taxonomy) => ({
      key: taxonomy.key,
      label: t.detail.taxonomy(taxonomy.key, taxonomy.name),
      prefix: taxonomy.prefix ?? '',
      multiple: taxonomy.cardinality === 'many',
      options: allTags
        .filter((tag) => tag.taxonomy_id === taxonomy.id)
        .map((tag) => ({ id: tag.id, name: tag.name })),
      value: tagsOf(taxonomy.id),
      onAdd: (tag) =>
        run(
          setBlockTags({
            block: { id: block.id, workspace_id: block.workspace_id },
            taxonomy: {
              id: taxonomy.id,
              workspace_id: taxonomy.workspace_id,
              cardinality: taxonomy.cardinality,
            },
            current: tagsOf(taxonomy.id).map((current) => ({ tag_id: current.id })),
            tags:
              taxonomy.cardinality === 'one'
                ? [tag.id ? { name: tag.name, existing_id: tag.id } : { name: tag.name }]
                : [
                    ...tagsOf(taxonomy.id).map((current) => ({
                      name: current.name,
                      existing_id: current.id,
                    })),
                    tag.id ? { name: tag.name, existing_id: tag.id } : { name: tag.name },
                  ],
          }),
        ),
      onRemove: (id) =>
        run(
          setBlockTags({
            block: { id: block.id, workspace_id: block.workspace_id },
            taxonomy: {
              id: taxonomy.id,
              workspace_id: taxonomy.workspace_id,
              cardinality: taxonomy.cardinality,
            },
            current: tagsOf(taxonomy.id).map((current) => ({ tag_id: current.id })),
            tags: tagsOf(taxonomy.id)
              .filter((current) => current.id !== id)
              .map((current) => ({ name: current.name, existing_id: current.id })),
          }),
        ),
    }));

  const fieldId = (key: string) => fieldDefs.find((def) => def.key === key)?.id;
  const valueOf = (key: string) => fieldValues.find((row) => row.field_id === fieldId(key));
  const personTaxonomy = taxonomies.find((taxonomy) => taxonomy.key === 'person');
  const waitingOn = valueOf('waiting_on')?.value_ref ?? null;
  const followUpOn = valueOf('follow_up_on')?.value_date ?? null;
  const personName = allTags.find((tag) => tag.id === waitingOn)?.name ?? null;

  const writeFollowUp = (person: { id?: string; name: string } | null, date: string | null) => {
    const waitingField = fieldId('waiting_on');
    const followField = fieldId('follow_up_on');
    if (!waitingField || !followField || !personTaxonomy) return;
    const chosen = person ?? (waitingOn && personName ? { id: waitingOn, name: personName } : null);
    if (!chosen || !date) return;
    const result = setFollowUp({
      block: { id: block.id, workspace_id: block.workspace_id },
      waiting_on: {
        taxonomy: {
          id: personTaxonomy.id,
          workspace_id: personTaxonomy.workspace_id,
          cardinality: personTaxonomy.cardinality,
        },
        path: [chosen.id ? { name: chosen.name, existing_id: chosen.id } : { name: chosen.name }],
      },
      follow_up_on: date,
      fields: { waiting_on: waitingField, follow_up_on: followField },
      previous: fieldValues
        .filter((row) => row.field_id === waitingField || row.field_id === followField)
        .map((row) => ({
          ...row,
          value_bool:
            row.value_bool === null ? null : row.value_bool === 1 ? (1 as const) : (0 as const),
          value_json: row.value_json === null ? null : (JSON.parse(row.value_json) as string),
        })),
    });
    run(result.operations);
  };

  const highlightReason =
    props.highlightType &&
    stage?.category === props.highlightType.eligible_stage_category &&
    block.kind === props.highlightType.eligible_kind
      ? null
      : t.asap.taiOnlyAsap;

  return (
    <DetailPanel
      layout={props.layout}
      kind={block.kind}
      kindLabel={block.kind === 'task' ? null : (t.convert.kinds[block.kind] ?? block.kind)}
      actions={{
        label: t.convert.menu,
        items: [
          {
            id: 'convert',
            label: t.convert.convertTo,
            items: targets.map((target) => ({
              id: target,
              label: t.convert.kinds[target] ?? target,
            })),
          },
          { id: 'trash', label: t.trash.delete },
        ],
        onChoose: (choice) => {
          if (choice === 'trash') {
            void props.actions.trash(block.id);
            props.onClose();
            return;
          }
          props.actions.convert(block.id, choice as (typeof targets)[number]);
        },
      }}
      link={
        block.kind === 'link'
          ? { url: linkUrlOf(block.props), onChange: () => props.actions.changeLink(block.id) }
          : null
      }
      breadcrumbs={{
        area: tags.find((row) => row.taxonomy_key === 'area')?.name ?? null,
        parent:
          block.parent_id && block.parent_title
            ? { id: block.parent_id, title: block.parent_title }
            : null,
      }}
      onOpenParent={props.onOpen}
      title={block.title}
      onRename={(title) => run(updateBlockTitle({ id: block.id, title }))}
      stage={{ name: stage?.name ?? '—', onMove: () => props.actions.openMove(block.id) }}
      done={block.status === 'done'}
      onToggleDone={() => void props.actions.toggle(block.id)}
      taxonomies={taxonomyFields}
      dates={{
        due: block.due_on,
        start: block.start_on,
        onDue: (due_on) =>
          run(
            updateBlockDates({
              block: { id: block.id, due_on: block.due_on, start_on: block.start_on },
              patch: { due_on },
            }),
          ),
        onStart: (start_on) =>
          run(
            updateBlockDates({
              block: { id: block.id, due_on: block.due_on, start_on: block.start_on },
              patch: { start_on },
            }),
          ),
      }}
      highlight={
        // A project, a note or a page has no TAI to show (D5); an element or a task explains why not.
        props.highlightType && (block.kind === 'task' || block.kind === 'item')
          ? {
              on: props.highlighted,
              usage: t.asap.taiUsage(props.highlightsUsed, props.highlightType.max_per_period),
              reason: highlightReason,
              onToggle: () => void props.actions.toggleHighlight(block.id),
            }
          : null
      }
      followUp={
        stage?.category === 'waiting' && personTaxonomy
          ? {
              person: {
                key: 'waiting_on',
                label: t.detail.waitingOn,
                prefix: '+',
                multiple: false,
                options: allTags
                  .filter((tag) => tag.taxonomy_id === personTaxonomy.id)
                  .map((tag) => ({ id: tag.id, name: tag.name })),
                value: waitingOn && personName ? [{ id: waitingOn, name: personName }] : [],
                onAdd: (person) => writeFollowUp(person, followUpOn ?? props.today),
                onRemove: () => undefined,
              },
              date: followUpOn,
              onDate: (date) => writeFollowUp(null, date),
            }
          : null
      }
      subtasks={{
        items: subtasks.map((child) => ({
          id: child.id,
          title: child.title,
          done: child.status === 'done',
        })),
        progress:
          subtasks.length > 0
            ? `${subtasks.filter((child) => child.status === 'done').length}/${subtasks.length}`
            : null,
        onToggle: (id) => void props.actions.toggle(id),
        onOpen: props.onOpen,
        onCreate: (title) =>
          run(
            createSubtask({
              parent: { id: block.id, workspace_id: block.workspace_id },
              created_by: props.userId,
              title,
              last_sibling_sort_key: subtasks[subtasks.length - 1]?.sort_key ?? null,
            }),
          ),
      }}
      note={{
        value: block.body,
        onSave: (body) => run(updateBlockBody({ block: { id: block.id, body: block.body }, body })),
      }}
      onClose={props.onClose}
      normalize={normalizeTagName}
      labels={t.detail.labels}
    />
  );
}
